import React, { useEffect, useMemo, useState } from 'react';
import { withRouter } from "react-router-dom";
import DataTable from 'react-data-table-component';
import ReportsV2 from '../Services/ReportsV2';
import Alert from '../Services/Alert';

const CURRENCY = process.env.REACT_APP_CURRENCY_SYMBOL || '₹';
const money = (v) => `${CURRENCY}${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Money currently held by users: the three wallet totals, plus who holds it.
 * Counts active users only — a deleted account's balance is not money the app still owes, and
 * is reported separately on the Deleted Users page.
 */
export const UserBalancesReport = (props) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('wallet');
    const [isReset, setIsReset] = useState(false);

    const dflt = { users: 0, wallet: 0, wallet_wining: 0, wallet_bonus: 0, grand_total: 0 };
    const [summary, setSummary] = useState(dflt);

    const columns = useMemo(() => [
        { name: 'Mobile', selector: row => row.mobile_no || '-', width: '140px' },
        { name: 'Name', selector: row => [row.firstname, row.lastname].filter(Boolean).join(' ') || '-', wrap: true },
        { name: 'Wallet', selector: row => money(row.wallet), right: true },
        { name: 'Winning', selector: row => money(row.wallet_wining), right: true },
        { name: 'Bonus', selector: row => money(row.wallet_bonus), right: true },
        { name: 'Total', selector: row => money(row.total_balance), right: true },
    ], []);

    function fetchReport(page, size = perPage) {
        setLoading(true);
        ReportsV2.userBalances({ page, size, search, sortBy }).then(resp => {
            const r = resp.data;
            if (r.status == 1) {
                setData(r.data.docs);
                setTotalRows(r.total);
                if (page === 1) setSummary(r.summaryData || dflt);
            } else Alert.error(r.message);
            setLoading(false);
        }).catch(err => { setLoading(false); Alert.error(err.message); });
    }

    useEffect(() => { fetchReport(1); }, [isReset]);

    const tile = (label, value, cls) => (
        <div className="col-lg-3 col-6 mb-3">
            <div className="border rounded p-3 h-100">
                <div className="text-muted" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
                <div className={`mt-1 ${cls || ''}`} style={{ fontSize: '20px', fontWeight: 600 }}>{value}</div>
            </div>
        </div>
    );

    return (
        <section className="main-content marketadd_main_sec">
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="card">
                                <div className="card-body">
                                    <h4 className="card-title mb-1">Users Balance</h4>
                                    <p className="text-muted">Money currently held by active users across all three wallets.</p>

                                    <div className="row">
                                        {tile('Main Wallet', money(summary.wallet))}
                                        {tile('Winning Wallet', money(summary.wallet_wining))}
                                        {tile('Bonus Wallet', money(summary.wallet_bonus))}
                                        {tile('Total Held', money(summary.grand_total), 'text-success')}
                                    </div>
                                    <p className="text-muted mb-4"><small>Across {Number(summary.users || 0).toLocaleString('en-IN')} active users.</small></p>

                                    <div className="border border-primary p-3 mb-4">
                                        <div className="row">
                                            <div className="col-lg-4">
                                                <label className="form-label">Search (mobile or name)</label>
                                                <input type="text" className="form-control" value={search}
                                                    onChange={e => setSearch(e.target.value)}
                                                    onKeyDown={e => { if (e.key === 'Enter') setIsReset(!isReset); }} />
                                            </div>
                                            <div className="col-lg-3">
                                                <label className="form-label">Sort by</label>
                                                <select className="form-control" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                                                    <option value="wallet">Main wallet</option>
                                                    <option value="wallet_wining">Winning wallet</option>
                                                    <option value="wallet_bonus">Bonus wallet</option>
                                                </select>
                                            </div>
                                            <div className="col-lg-4 d-flex align-items-end">
                                                <button className="btn btn-primary me-2" onClick={() => setIsReset(!isReset)}>Apply</button>
                                                <button className="btn btn-light" onClick={() => { setSearch(''); setSortBy('wallet'); setIsReset(!isReset); }}>Reset</button>
                                            </div>
                                        </div>
                                    </div>

                                    <DataTable
                                        columns={columns} data={data} progressPending={loading}
                                        pagination paginationServer paginationTotalRows={totalRows}
                                        onChangeRowsPerPage={(n, p) => { setPerPage(n); fetchReport(p, n); }}
                                        onChangePage={(p) => fetchReport(p)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default withRouter(UserBalancesReport);
